'use client';

import {
  type RumEvent,
  type RumEventDomainContext,
  type RumInitConfiguration,
  type RumResourceEvent,
  datadogRum,
} from '@datadog/browser-rum';
import {
  NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_AUTH_API_BASE_URL,
  NEXT_PUBLIC_DATADOG_APP_ID,
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  NEXT_PUBLIC_DATADOG_SITE,
  NEXT_PUBLIC_FUNCTIONS_URL,
  NEXT_PUBLIC_RELEASE_VERSION,
} from '@env';
import { getCookieConsentForCategory } from '@modules/cookie-utils';
import { botUserAgentPatterns } from '@modules/monitoring/constants';
import { type DatadogEnv, getDatadogEnv } from '@modules/monitoring/datadog-env';

export const DD_IMAGE_TYPE_URL_PARAM = 'blnq_dd_image_type';
export type DDImageType = 'profile_image' | 'logo_image' | 'cover_image';

const botPattern = `(${botUserAgentPatterns.join('|')})`;
const regex = new RegExp(botPattern, 'i');

function addImageResourceTypeTag(event: RumEvent, _: RumEventDomainContext): boolean {
  if (event.type === 'resource' && (event as RumResourceEvent).resource.url) {
    const url = new URL(event.resource.url);
    if (url.searchParams.has(DD_IMAGE_TYPE_URL_PARAM)) {
      if (!event.context) {
        event.context = {};
      }
      event.context.image_type = url.searchParams.get(DD_IMAGE_TYPE_URL_PARAM);
    }
  }
  return true;
}

/**
 * Origin of a backend base URL with a trailing slash (so the match is host-anchored — it can't be a
 * prefix of a longer host like `…blnq.xyz.evil.com`), or `null` when the env var is unset/malformed.
 */
function tracedOrigin(baseUrl: string): string | null {
  try {
    return baseUrl ? `${new URL(baseUrl).origin}/` : null;
  } catch {
    return null;
  }
}

/**
 * First-party Blinq backends the dashboard calls directly from the browser that run dd-trace, so
 * distributed traces correlate end-to-end. Derived from the same env vars the app uses to reach them,
 * so the set tracks every environment (prod / nonprod / preview / local) automatically and can never
 * drift from where requests actually go.
 *
 * This is an explicit allowlist, not a broad "*.blinq.me minus a denylist". Anything our domains front
 * that is NOT one of these is therefore never traced — which is the point: their CORS rejects the
 * injected `x-datadog-*` / `baggage` headers (failing the preflight) and they run no dd-trace anyway:
 *   - Stytch Connected Apps IdP — `NEXT_PUBLIC_STYTCH_CUSTOM_DOMAIN` (idp.blinq.me / idp-test.blnq.xyz)
 *   - the Amplitude analytics-proxy worker (`ae.*`) and the icon worker (`NEXT_PUBLIC_CF_ICONS_WORKER_URL`)
 *   - the forward / QR / deep-link / image-CDN Cloudflare workers, and every third-party SDK endpoint
 *
 * This replaces a denylist regex (`*.blnq.xyz` minus first-label `idp.`) that silently broke when the
 * nonprod IdP domain turned out to be the hyphenated `idp-test.blnq.xyz` — first label `idp-test`, not
 * `idp`, so the lookahead missed it and tracing headers were injected into Stytch, failing every OAuth
 * consent preflight. An allowlist can't regress that way: a new third-party host on our domain is
 * untraced by default, and only the backends listed here are ever traced.
 */
export const firstPartyTracedOrigins: string[] = [
  NEXT_PUBLIC_API_URL, // api: GraphQL (HTTP) + REST
  NEXT_PUBLIC_AUTH_API_BASE_URL, // auth-service OAuth broker (/trusted-token/attest, /email/link)
  NEXT_PUBLIC_FUNCTIONS_URL, // Firebase Cloud Functions
]
  .map(tracedOrigin)
  .filter((origin): origin is string => origin !== null);

/** Whether Datadog should inject distributed-tracing headers into a cross-origin request to `url`. */
export function isFirstPartyTracedUrl(url: string): boolean {
  return firstPartyTracedOrigins.some((origin) => url.startsWith(origin));
}

const sharedRumInitConfig: RumInitConfiguration = {
  applicationId: NEXT_PUBLIC_DATADOG_APP_ID,
  clientToken: NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  site: NEXT_PUBLIC_DATADOG_SITE,
  service: NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  version: NEXT_PUBLIC_RELEASE_VERSION,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  // Which outgoing requests get Datadog tracing headers injected — the explicit allowlist of
  // first-party dd-trace backends (see `firstPartyTracedOrigins`). Same-origin is always traced; every
  // cross-origin host not on the allowlist (Google APIs, Stytch IdP, CF workers, third-party SDKs) is
  // left alone so we never fail their CORS preflights with `x-datadog-*` headers they don't accept.
  allowedTracingUrls: [
    // Same-origin (the Next.js server) — dash.blinq.me, dash.nonprod.blnq.xyz, Vercel previews, and
    // localhost — without hard-coding hostnames.
    (url: string) => typeof window !== 'undefined' && url.startsWith(window.location.origin),
    ...firstPartyTracedOrigins,
  ],
  beforeSend: addImageResourceTypeTag,
};

const rumInitConfigs: { [key in DatadogEnv]: RumInitConfiguration } = {
  production: {
    ...sharedRumInitConfig,
    env: 'production',
    sessionSampleRate: typeof window !== 'undefined' && regex.test(navigator.userAgent) ? 0 : 100,
    sessionReplaySampleRate:
      typeof window !== 'undefined' && regex.test(navigator.userAgent) ? 0 : 100,
    trackingConsent: getCookieConsentForCategory('performance') ? 'granted' : 'not-granted',
  },
  'non-production': {
    ...sharedRumInitConfig,
    env: 'non-production',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackingConsent: getCookieConsentForCategory('performance') ? 'granted' : 'not-granted',
  },
  preview: {
    ...sharedRumInitConfig,
    env: 'preview',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackingConsent: getCookieConsentForCategory('performance') ? 'granted' : 'not-granted',
  },
};

/**
 * Resolve the RUM init configuration for the current deployment environment.
 *
 * Returns `null` in environments where we don't emit RUM (local dev). The
 * actual `datadogRum.init` call lives in `instrumentation-client.ts` so that
 * the `@datadog/browser-rum-nextjs` plugin can wire up Next-aware route
 * tracking at the earliest possible client boot.
 */
export function getRumInitConfig(): RumInitConfiguration | null {
  const datadogEnv = getDatadogEnv();
  return datadogEnv ? rumInitConfigs[datadogEnv] : null;
}

export const blinqDatadogRum = datadogRum;
